import { useEffect, useRef } from "react";

import type { HandCardView, PlayerViewId } from "../presentation/game-view-model";
import { formatResources } from "../presentation/resource-format";

interface HandPanelProps {
  activePlayerId: PlayerViewId;
  cards: readonly HandCardView[];
  selectedCardInstanceId: string | undefined;
  validationMessage?: string | undefined;
  onSelectCard: (cardInstanceId: string) => void;
  onCancelSelection: () => void;
}

function playerLabel(playerId: PlayerViewId) {
  return playerId === "player-1" ? "Your hand" : "Opponent turn hand";
}

function commanderActionLabel(
  targets: NonNullable<HandCardView["commander"]>["targets"],
): "Deploy" | "Replace" | "Deploy or Replace" {
  const deployAvailable = targets.some((target) => target.actionType === "deploy-commander");
  const replaceAvailable = targets.some((target) => target.actionType === "replace-commander");
  if (deployAvailable && replaceAvailable) return "Deploy or Replace";
  if (replaceAvailable) return "Replace";
  return "Deploy";
}

function commanderGuidance(card: HandCardView, validationMessage: string | undefined): string {
  if (card.commander === undefined) return "";
  if (validationMessage !== undefined) return validationMessage;

  const legalTargets = card.commander.targets.filter((target) => target.legal);
  if (legalTargets.length > 0) {
    const actionLabel = commanderActionLabel(legalTargets);
    return `${legalTargets.length} legal Land target${legalTargets.length === 1 ? "" : "s"}. Tap a highlighted ${actionLabel} target.`;
  }
  const rejectionMessages = [
    ...new Set(
      card.commander.targets.flatMap((target) =>
        target.rejectionMessage === undefined ? [] : [target.rejectionMessage],
      ),
    ),
  ];
  const actionLabel = commanderActionLabel(card.commander.targets).toLowerCase();
  return `Cannot ${actionLabel}: ${rejectionMessages.join(" ") || "No engine-legal Land target is available."}`;
}

function armyGuidance(card: HandCardView, validationMessage: string | undefined): string {
  if (card.army === undefined) return "";
  if (validationMessage !== undefined) return validationMessage;

  const legalTargets = card.army.targets.filter((target) => target.legal);
  if (legalTargets.length > 0) {
    const warningCount = legalTargets.filter(
      (target) => target.projectedNextSupplySustainable === false,
    ).length;
    return `${legalTargets.length} legal commanded Land target${legalTargets.length === 1 ? "" : "s"}. Tap a highlighted Deploy Army target.${warningCount === 0 ? "" : ` ${warningCount} target${warningCount === 1 ? " has" : "s have"} a next-Supply warning, but deployment remains legal.`}`;
  }
  const rejectionMessages = [
    ...new Set(
      card.army.targets.flatMap((target) =>
        target.rejectionMessage === undefined ? [] : [target.rejectionMessage],
      ),
    ),
  ];
  return `Cannot deploy Army: ${rejectionMessages.join(" ") || "No engine-legal commanded Land target is available."}`;
}

export function HandPanel({
  activePlayerId,
  cards,
  selectedCardInstanceId,
  validationMessage,
  onSelectCard,
  onCancelSelection,
}: HandPanelProps) {
  const selectedCard = cards.find((card) => card.instanceId === selectedCardInstanceId);
  const selectedCardButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    selectedCardButtonRef.current?.focus({ preventScroll: true });
  }, [selectedCardInstanceId]);

  if (cards.length === 0) return null;

  return (
    <section
      className="hand-panel"
      aria-label={playerLabel(activePlayerId)}
      data-testid="hand-panel"
    >
      <div className="hand-panel__heading">
        <span className="eyebrow">{playerLabel(activePlayerId)}</span>
        <span>{cards.length} cards</span>
      </div>

      <div className="hand-panel__cards">
        {cards.map((card) => {
          const selected = card.instanceId === selectedCardInstanceId;
          const isLand = card.type === "land";
          const isCommander = card.commander !== undefined;
          const isArmy = card.army !== undefined;

          return (
            <button
              key={card.instanceId}
              className="hand-card"
              type="button"
              ref={selected ? selectedCardButtonRef : undefined}
              aria-pressed={selected}
              aria-label={`${card.name}, ${isLand ? "Land" : card.type}`}
              data-card-type={card.type}
              data-testid={`hand-card-${card.instanceId}`}
              disabled={!isLand && !isCommander && !isArmy}
              onClick={() => onSelectCard(card.instanceId)}
            >
              <span className="hand-card__type">{isLand ? "Land" : card.type}</span>
              <strong>{card.name}</strong>
              <span>
                {isLand
                  ? `Stage I ${card.stages?.[0]?.maximumControl ?? "—"}`
                  : card.commander
                    ? `A ${card.commander.attack} · G ${card.commander.guard} · C ${card.commander.armyCapacity}`
                    : card.army
                      ? `S ${card.army.armySize} · A ${card.army.attack} · G ${card.army.guard}`
                      : "Deferred"}
              </span>
            </button>
          );
        })}
      </div>

      {selectedCard?.type === "land" ? (
        <aside
          className="selected-card"
          aria-label="Selected Land details"
          data-testid="selected-card"
        >
          <div>
            <span className="eyebrow">Selected Land</span>
            <strong>{selectedCard.name}</strong>
            <span className="selected-card__identity">{selectedCard.developmentId}</span>
          </div>
          <div className="selected-card__stages" aria-label="Land Stage maximum Control">
            {selectedCard.stages?.map((stage) => (
              <span key={stage.stage}>
                S{stage.stage} <strong>{stage.maximumControl}</strong>
              </span>
            ))}
          </div>
          <button className="icon-button" type="button" onClick={onCancelSelection}>
            Cancel
          </button>
        </aside>
      ) : null}

      {selectedCard?.commander ? (
        <aside
          className="selected-card selected-card--commander"
          aria-label="Selected Commander details"
          data-testid="selected-commander"
        >
          <div>
            <span className="eyebrow">Selected Commander</span>
            <strong>{selectedCard.name}</strong>
            <span className="selected-card__commander-cost">
              Cost {formatResources(selectedCard.commander.cost)}
            </span>
          </div>
          <div className="selected-card__commander-stats" aria-label="Printed Commander values">
            <span>
              Attack <strong>{selectedCard.commander.attack}</strong>
            </span>
            <span>
              Guard <strong>{selectedCard.commander.guard}</strong>
            </span>
            <span>
              Capacity <strong>{selectedCard.commander.armyCapacity}</strong>
            </span>
          </div>
          <button className="icon-button" type="button" onClick={onCancelSelection}>
            Cancel
          </button>
          <p className="selected-card__commander-guidance" role="status">
            {commanderGuidance(selectedCard, validationMessage)}
          </p>
        </aside>
      ) : null}

      {selectedCard?.army ? (
        <aside
          className="selected-card selected-card--commander selected-card--army"
          aria-label="Selected Army details"
          data-testid="selected-army"
        >
          <div>
            <span className="eyebrow">Selected Army</span>
            <strong>{selectedCard.name}</strong>
            <span className="selected-card__commander-cost">
              Cost {formatResources(selectedCard.army.cost)} · Upkeep{" "}
              {formatResources(selectedCard.army.upkeep)}
            </span>
          </div>
          <div className="selected-card__commander-stats" aria-label="Printed Army values">
            <span>
              Size <strong>{selectedCard.army.armySize}</strong>
            </span>
            <span>
              Attack <strong>{selectedCard.army.attack}</strong>
            </span>
            <span>
              Guard <strong>{selectedCard.army.guard}</strong>
            </span>
          </div>
          <button className="icon-button" type="button" onClick={onCancelSelection}>
            Cancel
          </button>
          <p className="selected-card__commander-guidance" role="status">
            {armyGuidance(selectedCard, validationMessage)}
          </p>
        </aside>
      ) : null}
    </section>
  );
}
